import { Alert, Box, Button, IconButton, InputAdornment, InputLabel, TextField, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { forgotPasswordResetFormValidationSchema } from "validations/schemas";
import { useEffect, useState } from "react";
import {
    CORPORATE_BACKOFFICE_PATHS,
    LocalStorageGlobalKeys,
    RESET_PASSWORD_ERROR_CODES,
    ServerResponseErrorCodes,
} from "constants/constants";
import { forgotPasswordReset } from "api/queries/usersAPI";
import { IForgotPasswordResetValues } from "./ForgotPasswordResetInterfaces";
import { useStyles } from "./ForgotPasswordReset.styles";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";

const ForgotPasswordReset = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isTokenEmailInvalid, setIsTokenEmailInvalid] = useState(false);
    const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get(LocalStorageGlobalKeys.TOKEN);

    useEffect(() => {
        if (!token) {
            navigate(`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.FORGOT_PASSWORD.ROOT}`);
        }
    }, [navigate, token]);

    const initFormValues: IForgotPasswordResetValues = {
        newPassword: "",
        confirmPassword: "",
    };

    const handleChangePasswordSubmit = async (values: IForgotPasswordResetValues) => {
        setIsSaving(true);
        try {
            const newValues: IForgotPasswordResetValues & { token: string } = {
                newPassword: values.newPassword,
                confirmPassword: values.confirmPassword,
                token: token || "",
            };

            await forgotPasswordReset(newValues);
            setChangePasswordSuccess(true);
        } catch (error: any) {
            const { response } = error;

            if (
                response &&
                response.status === ServerResponseErrorCodes.FORBIDDEN &&
                response.data.errorCode &&
                response.data.errorCode === RESET_PASSWORD_ERROR_CODES.TOKEN_INVALID
            ) {
                setIsTokenEmailInvalid(true);
            }

            setIsSaving(false);
        }
    };

    return (
        <section>
            <section className={classes.mainWrapper}>
                <div className={classes.contentWrapper}>
                    <Typography className={classes.headerTitle}>{t("forgot_password_reset.title")}</Typography>

                    {!isTokenEmailInvalid && !changePasswordSuccess && (
                        <>
                            <Alert severity="info" className={classes.alert}>
                                <Typography>{t("forgot_password_reset.enter_new_password")}</Typography>
                            </Alert>

                            <Formik
                                initialValues={initFormValues}
                                validationSchema={forgotPasswordResetFormValidationSchema}
                                onSubmit={handleChangePasswordSubmit}
                            >
                                {({ errors, touched, values, handleChange }) => {
                                    return (
                                        <Form className={classes.formContainer}>
                                            <>
                                                <Box className={classes.boxContainer}>
                                                    <InputLabel id="new-password-label">
                                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                                            {t("forgot_password_reset.new_password")}
                                                        </Typography>
                                                    </InputLabel>
                                                    <TextField
                                                        aria-labelledby="new-password-label"
                                                        fullWidth
                                                        id="new-password-input"
                                                        className={classes.textField}
                                                        name="newPassword"
                                                        type={showNewPassword ? "text" : "password"}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            setShowNewPassword(!showNewPassword)
                                                                        }
                                                                        onMouseDown={(event) => event.preventDefault()}
                                                                        edge="end"
                                                                    >
                                                                        {showNewPassword ? (
                                                                            <VisibilityOff />
                                                                        ) : (
                                                                            <Visibility />
                                                                        )}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        disabled={isSaving}
                                                        variant="outlined"
                                                        autoFocus={true}
                                                        value={values.newPassword}
                                                        onChange={handleChange}
                                                        error={touched.newPassword && Boolean(errors.newPassword)}
                                                        helperText={
                                                            touched.newPassword &&
                                                            errors.newPassword &&
                                                            t(errors.newPassword)
                                                        }
                                                    />
                                                </Box>
                                                <Box className={classes.boxContainer}>
                                                    <InputLabel id="current-password-label">
                                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                                            {t("forgot_password_reset.confirm_new_password")}
                                                        </Typography>
                                                    </InputLabel>
                                                    <TextField
                                                        aria-labelledby="confirm-password-label"
                                                        fullWidth
                                                        id="confirm-password-input"
                                                        className={classes.textField}
                                                        name="confirmPassword"
                                                        disabled={isSaving}
                                                        type={showConfirmPassword ? "text" : "password"}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            setShowConfirmPassword(!showConfirmPassword)
                                                                        }
                                                                        onMouseDown={(event) => event.preventDefault()}
                                                                        edge="end"
                                                                    >
                                                                        {showConfirmPassword ? (
                                                                            <VisibilityOff />
                                                                        ) : (
                                                                            <Visibility />
                                                                        )}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        variant="outlined"
                                                        autoFocus={true}
                                                        value={values.confirmPassword}
                                                        onChange={handleChange}
                                                        error={
                                                            touched.confirmPassword && Boolean(errors.confirmPassword)
                                                        }
                                                        helperText={
                                                            touched.confirmPassword &&
                                                            errors.confirmPassword &&
                                                            t(errors.confirmPassword)
                                                        }
                                                    />
                                                </Box>

                                                <div className={classes.footer}>
                                                    <Button
                                                        variant="outlined"
                                                        color="info"
                                                        className={classes.submitButton}
                                                        onClick={() => navigate(CORPORATE_BACKOFFICE_PATHS.LOGIN)}
                                                        disabled={isSaving}
                                                    >
                                                        {t("forgot_password_reset.back_to_login")}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="info"
                                                        type="submit"
                                                        className={classes.submitButton}
                                                        disabled={isSaving}
                                                    >
                                                        {t("forgot_password_reset.submit")}
                                                    </Button>
                                                </div>
                                            </>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </>
                    )}
                    {isTokenEmailInvalid && (
                        <>
                            <Alert severity="error" className={classes.alert}>
                                <Typography>{t("forgot_password_reset.token_invalid_or_expired")}</Typography>
                            </Alert>
                            <Button
                                variant="outlined"
                                color="info"
                                className={classes.submitButton}
                                onClick={() => navigate(CORPORATE_BACKOFFICE_PATHS.LOGIN)}
                            >
                                {t("forgot_password_reset.back_to_login")}
                            </Button>
                        </>
                    )}
                    {changePasswordSuccess && (
                        <>
                            <Alert severity="success" className={classes.alert}>
                                <Typography>{t("forgot_password_reset.password_changed")}</Typography>
                            </Alert>
                            <Button
                                variant="outlined"
                                color="info"
                                className={classes.submitButton}
                                onClick={() => navigate(CORPORATE_BACKOFFICE_PATHS.LOGIN)}
                            >
                                {t("forgot_password_reset.back_to_login")}
                            </Button>
                        </>
                    )}
                </div>
            </section>
        </section>
    );
};

export default ForgotPasswordReset;
